import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, takeNext } from "@/lib/auth";

export const startGoogleSignIn = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export function GoogleButton({ label = "Continue with Google" }) {
  return (
    <button type="button" data-testid="google-signin-btn" onClick={startGoogleSignIn}
      className="w-full h-11 border border-foreground rounded-sm flex items-center justify-center gap-3 text-sm font-medium bg-background hover:bg-muted transition-colors">
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
        <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/>
      </svg>
      {label}
    </button>
  );
}

export function OrDivider() {
  return (
    <div className="flex items-center gap-3 font-mono-label text-muted-foreground">
      <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
    </div>
  );
}

export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { acceptSession } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", location.pathname + location.search);
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        acceptSession(data);
        toast.success(`Signed in as ${data.user.email}`);
        const { resumePendingPlan } = await import("@/components/Pricing");
        if (await resumePendingPlan()) return;
        nav(takeNext(), { replace: true });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Google sign-in failed. Please try again.");
        nav("/login", { replace: true });
      }
    })();
  }, [location, nav, acceptSession]);

  return (
    <div data-testid="auth-callback" className="min-h-screen flex items-center justify-center font-mono-label text-muted-foreground">
      Signing you in…
    </div>
  );
}
